import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Spin, Row, Col, Table, Tag, Button, message, Space, Modal, Input, InputNumber,
  Select, Popconfirm, Empty, Tooltip, Divider, DatePicker, Descriptions, Switch,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, ThunderboltOutlined,
  CalculatorOutlined, AppstoreOutlined, SaveOutlined, SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { yuan } from '../utils/format.js';
import { apiFetch, notifyDataChanged, onDataChanged } from '../utils/api.js';

// 判定 → 语义色（国内红涨绿跌惯例：加预算=好→红，砍链接=差→绿）
const VERDICT_META = {
  '加预算': { color: 'red', bg: '#fff1f0', desc: '投产比 ≥ 保本线，可加大预算' },
  '优化': { color: 'orange', bg: '#fff7e6', desc: '有利润但未达保本线，优化转化' },
  '砍链接': { color: 'green', bg: '#f6ffed', desc: '扣费后亏损，建议停投' },
  '未投': { color: 'default', bg: '#fafafa', desc: '未投放推广' },
};

const EMPTY_FORM = { name: '', shop: [], fee: 0, revenue: 0, profit: 0, breakEven: 0 };

export default function LinkAttribution() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // 关联商品自动带出：日期范围 + 已选店铺 → 系统订单按规格编码聚合的营收/利润
  const [dateStart, setDateStart] = useState(dayjs().startOf('month'));
  const [dateEnd, setDateEnd] = useState(null);      // null = 实时滚动到今天
  const [liveEnd, setLiveEnd] = useState(true);      // 结束日期是否自动滚动到实时
  const [productOpts, setProductOpts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [linkedSpecs, setLinkedSpecs] = useState([]); // 已关联商品编码（多选）
  // SKU 组合：把多个产品编码归成命名组合，链接一键带入
  const [skuGroups, setSkuGroups] = useState([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupSaving, setGroupSaving] = useState(false);
  // 详情弹窗 + 盈亏计算带入保本投产比
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pcOptions, setPcOptions] = useState([]); // 盈亏计算保存记录（含 break_even_roi）

  const load = () => apiFetch('/api/ad-link-attribution')
    .then(r => r.json())
    .then(d => { setData(d.data || []); setLoading(false); })
    .catch(e => { message.error(e.message || '加载失败'); setLoading(false); });

  useEffect(() => {
    apiFetch('/api/filters').then(r => r.json()).then(d => setShops(d.shops || [])).catch(() => {});
    apiFetch('/api/profit-calc').then(r => r.json()).then(d => setPcOptions(d.data || [])).catch(() => {});
    apiFetch('/api/sku-groups').then(r => r.json()).then(d => setSkuGroups(d.data || [])).catch(() => {});
    load();
    return onDataChanged(() => { setLoading(false); load(); });
  }, []);

  // 拉取关联商品统计（按开始/结束日期 + 已选店铺；end 为空 = 实时滚动到今天）
  const fetchProducts = (start, end, shopArr) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start.format('YYYY-MM-DD'));
    if (end) params.set('end', end.format('YYYY-MM-DD'));
    if (shopArr && shopArr.length) params.set('shops', shopArr.join(','));
    setProdLoading(true);
    apiFetch('/api/ad-link-attribution/product-stats?' + params.toString())
      .then(r => r.json())
      .then(d => setProductOpts(d.data || []))
      .catch(() => {})
      .finally(() => setProdLoading(false));
  };
  const onStartChange = (v) => { const s = v || dayjs().startOf('month'); setDateStart(s); fetchProducts(s, liveEnd ? null : dateEnd, form.shop); };
  const onEndChange = (v) => { setDateEnd(v || null); fetchProducts(dateStart, v || null, form.shop); };
  const onLiveEndChange = (v) => { setLiveEnd(v); fetchProducts(dateStart, v ? null : dateEnd, form.shop); };
  const onShopsChange = (v) => { setForm(f => ({ ...f, shop: v || [] })); fetchProducts(dateStart, liveEnd ? null : dateEnd, v); };

  // 汇总指标
  const summary = useMemo(() => {
    const totalFee = data.reduce((s, x) => s + (Number(x.fee) || 0), 0);
    const totalRev = data.reduce((s, x) => s + (Number(x.revenue) || 0), 0);
    const totalNet = data.reduce((s, x) => s + (Number(x.netProfit) || 0), 0);
    const avgRatio = totalFee > 0 ? totalRev / totalFee : 0;
    const dist = { '加预算': 0, '优化': 0, '砍链接': 0, '未投': 0 };
    for (const x of data) { if (dist[x.verdict] != null) dist[x.verdict] += 1; }
    return { totalFee, totalRev, totalNet, avgRatio, dist };
  }, [data]);

  // 详情弹窗展示数据（投产比 vs 保本线对比条）
  const detailView = useMemo(() => {
    if (!detailRow) return null;
    const ratio = Number(detailRow.ratio) || 0;
    const be = Number(detailRow.breakEven) || 0;
    const maxVal = Math.max(ratio, be, 1);
    return {
      ratio, be,
      ratioPct: Math.min(100, (ratio / maxVal) * 100),
      bePct: Math.min(100, (be / maxVal) * 100),
      shopText: Array.isArray(detailRow.shop) ? detailRow.shop.join('、') : (detailRow.shop || ''),
    };
  }, [detailRow]);

  // 根据已选编码，从 productOpts 聚合营收/利润（未扣推广费）
  const aggregatePicked = (specs) => {
    let rev = 0, prof = 0;
    for (const s of specs) {
      const p = productOpts.find(x => x.spec_code === s);
      if (p) { rev += Number(p.revenue) || 0; prof += Number(p.profit) || 0; }
    }
    return { rev, prof };
  };

  const openCreate = () => {
    setEditingId(null); setForm(EMPTY_FORM); setLinkedSpecs([]);
    const start = dayjs().startOf('month');
    setDateStart(start);
    setDateEnd(null);
    setLiveEnd(true);
    setModalOpen(true);
    fetchProducts(start, null, []);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    const specs = Array.isArray(row.spec_codes) ? row.spec_codes : (row.spec_code ? [row.spec_code] : []);
    setForm({
      name: row.name,
      shop: Array.isArray(row.shop) ? row.shop : (row.shop ? [row.shop] : []),
      fee: Number(row.fee) || 0, revenue: Number(row.revenue) || 0,
      profit: Number(row.profit) || 0, breakEven: Number(row.breakEven) || 0,
    });
    setLinkedSpecs(specs);
    const start = row.date_start ? dayjs(row.date_start) : dayjs().startOf('month');
    const end = row.date_end ? dayjs(row.date_end) : null;
    const isLive = !row.date_end;
    setDateStart(start);
    setDateEnd(end);
    setLiveEnd(isLive);
    setModalOpen(true);
    fetchProducts(start, end, Array.isArray(row.shop) ? row.shop : (row.shop ? [row.shop] : []));
  };
  // 保本投产比自动计算：= 营收 ÷ 利润（即 1 / 毛利率）
  const autoBreakEven = () => {
    const p = Number(form.profit) || 0;
    const r = Number(form.revenue) || 0;
    if (p <= 0) { message.warning('利润需 > 0 才能自动计算保本投产比'); return; }
    setForm(f => ({ ...f, breakEven: Math.round((r / p) * 100) / 100 }));
    message.success('已按 营收÷利润 自动填入保本投产比');
  };
  // 从盈亏计算器带入保本投产比（精确版，含退货损失）
  const pickBreakEvenFromCalc = (id) => {
    if (id == null) return;
    const c = pcOptions.find(x => String(x.id) === String(id));
    if (!c) return;
    if (c.break_even_roi == null) { message.warning('该盈亏计算记录暂未算出保本投产比'); return; }
    setForm(f => ({ ...f, breakEven: c.break_even_roi }));
    message.success(`已带入「${c.name}」的保本投产比 ${c.break_even_roi}`);
  };
  // 详情弹窗
  const openDetail = (row) => { setDetailRow(row); setDetailOpen(true); };

  // 多选商品：自动带出系统订单营收/利润的合计（可手动微调）
  const pickProducts = (specs) => {
    setLinkedSpecs(specs || []);
    if (!specs || !specs.length) return;
    const { rev, prof } = aggregatePicked(specs);
    if (rev === 0 && prof === 0) return;
    setForm(f => ({ ...f, revenue: Math.round(rev * 100) / 100, profit: Math.round(prof * 100) / 100 }));
    message.success(`已自动带出 ${specs.length} 个商品合计营收 ¥${yuan(rev)} / 利润 ¥${yuan(prof)}（可手动微调）`);
  };

  // 从 SKU 组合一键带入多个商品
  const pickGroup = (groupId) => {
    if (groupId == null) return;
    const g = skuGroups.find(x => String(x.id) === String(groupId));
    if (!g) return;
    setLinkedSpecs(g.spec_codes || []);
    const { rev, prof } = aggregatePicked(g.spec_codes || []);
    setForm(f => ({ ...f, revenue: Math.round(rev * 100) / 100, profit: Math.round(prof * 100) / 100 }));
    message.success(`已带入组合「${g.name}」（${(g.spec_codes || []).length} 个 SKU）`);
  };

  // 保存当前多选为组合
  const openSaveGroup = () => {
    if (!linkedSpecs.length) { message.warning('请先在上方「关联商品」选择至少一个商品'); return; }
    setGroupName('');
    setGroupModalOpen(true);
  };
  const saveGroup = async () => {
    const name = String(groupName || '').trim();
    if (!name) { message.warning('请填写组合名称'); return; }
    setGroupSaving(true);
    try {
      const res = await apiFetch('/api/sku-groups', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, spec_codes: linkedSpecs }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || '保存失败');
      message.success(`已保存组合「${name}」`);
      setSkuGroups(prev => [...prev, j.data]);
      setGroupModalOpen(false);
    } catch (e) { message.error(e.message || '保存失败'); } finally { setGroupSaving(false); }
  };

  const save = async () => {
    const name = String(form.name || '').trim();
    if (!name) { message.warning('请填写推广链接/计划名称'); return; }
    setSaving(true);
    try {
      const body = {
        name, shop: form.shop, spec_codes: linkedSpecs, fee: form.fee, revenue: form.revenue, profit: form.profit, breakEven: form.breakEven,
        date_start: dateStart ? dateStart.format('YYYY-MM-DD') : '',
        date_end: (liveEnd || !dateEnd) ? '' : dateEnd.format('YYYY-MM-DD'),
      };
      const res = editingId != null
        ? await apiFetch(`/api/ad-link-attribution/${editingId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        : await apiFetch('/api/ad-link-attribution', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || '保存失败');
      message.success(editingId != null ? '已更新' : '已新增');
      setModalOpen(false);
      notifyDataChanged();
      load();
    } catch (e) { message.error(e.message || '保存失败'); } finally { setSaving(false); }
  };

  const remove = async (row) => {
    try {
      const res = await apiFetch(`/api/ad-link-attribution/${row.id}`, { method: 'DELETE' });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || '删除失败');
      message.success('已删除');
      notifyDataChanged();
      load();
    } catch (e) { message.error(e.message || '删除失败'); }
  };

  // 一键刷新：按每条记录自己的日期窗口 + 店铺 + 关联商品重新聚合最新营收/利润
  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await apiFetch('/api/ad-link-attribution/refresh', { method: 'POST' });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.error || '刷新失败');
      message.success(`已刷新 ${j.updated} 条记录为最新数据${j.skipped ? `（${j.skipped} 条无关联商品已跳过）` : ''}`);
      load();
    } catch (e) { message.error(e.message || '刷新失败'); }
    finally { setRefreshing(false); }
  };

  const columns = [
    {
      title: '推广链接 / 计划', dataIndex: 'name', fixed: 'left', width: 200,
      render: (v, r) => {
        const shopText = Array.isArray(r.shop) ? r.shop.join('、') : (r.shop || '');
        const specs = Array.isArray(r.spec_codes) ? r.spec_codes : (r.spec_code ? [r.spec_code] : []);
        const sub = shopText || (specs.length ? `${specs.length} 个 SKU` : '');
        const specText = specs.length ? specs.join('、') : '';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LinkOutlined style={{ color: 'var(--brand)' }} />
            <div style={{ minWidth: 0 }}>
              <div
                style={{ fontWeight: 600, color: 'var(--brand)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                onClick={() => openDetail(r)}
                title="点击查看详情"
              >
                {v}
              </div>
              <div style={{ fontSize: 12, color: '#B3A79C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={specText}>
                {sub}{specs.length ? `${sub ? ' · ' : ''}${specs.length} SKU` : ''}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: '建立时间', dataIndex: 'created_at', width: 135, align: 'center',
      sorter: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
      render: v => v ? <span style={{ color: '#8C8178' }}>{dayjs(v).format('YYYY-MM-DD HH:mm')}</span> : '—',
    },
    { title: '推广费', dataIndex: 'fee', width: 100, align: 'right', sorter: (a, b) => a.fee - b.fee, render: v => `¥${yuan(v)}` },
    { title: '营收', dataIndex: 'revenue', width: 110, align: 'right', sorter: (a, b) => a.revenue - b.revenue, defaultSortOrder: 'descend', render: v => <span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(v)}</span> },
    { title: '单量', dataIndex: 'orders', width: 80, align: 'right', sorter: (a, b) => (Number(a.orders) || 0) - (Number(b.orders) || 0), render: v => `${Number(v) || 0}单` },
    {
      title: '利润（已扣售后）', dataIndex: 'profit', width: 130, align: 'right', sorter: (a, b) => a.profit - b.profit,
      render: v => <span className={(Number(v) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(v)}</span>,
    },
    {
      title: '扣费后利润', dataIndex: 'netProfit', width: 110, align: 'right', sorter: (a, b) => a.netProfit - b.netProfit,
      render: v => <span className={(Number(v) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(v)}</span>,
    },
    {
      title: '真实投产比', dataIndex: 'ratio', width: 100, align: 'right', sorter: (a, b) => a.ratio - b.ratio,
      render: v => <span style={{ fontWeight: 700 }}>{v}</span>,
    },
    {
      title: '保本投产比', dataIndex: 'breakEven', width: 100, align: 'right', sorter: (a, b) => a.breakEven - b.breakEven,
      render: v => <span style={{ color: '#8C8178' }}>{v || '—'}</span>,
    },
    {
      title: '付费占比', dataIndex: 'payPct', width: 90, align: 'right', sorter: (a, b) => a.payPct - b.payPct,
      render: v => `${v}%`,
    },
    {
      title: '判定', dataIndex: 'verdict', width: 110, align: 'center', fixed: 'right',
      render: v => {
        const m = VERDICT_META[v] || VERDICT_META['未投'];
        return (
          <Tooltip title={m.desc}>
            <Tag color={m.color} style={{ fontSize: 13, padding: '2px 12px', borderRadius: 999 }}>{v}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '操作', width: 100, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Space size={0}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确定删除该推广链接？" okText="删除" cancelText="取消" onConfirm={() => remove(r)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#26201C' }}>推广归因</div>
          <div style={{ color: '#8C8178', fontSize: 12, marginTop: 4 }}>
            按推广链接 / 计划维度判断预算去留：真实投产比 = 营收 ÷ 推广费，扣费后利润 = 利润 − 推广费；利润已按售后扣除（签收/拦截/部分退款、补发损失）
          </div>
        </div>
        <Space>
          <Button icon={<SyncOutlined />} loading={refreshing} onClick={refreshAll}>一键刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增推广链接</Button>
        </Space>
      </div>

      {/* 汇总统计卡 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': 'var(--brand)' }}>
          <div className="stat-label">总推广费</div>
          <div className="stat-value" style={{ color: 'var(--brand)' }}>¥{yuan(summary.totalFee)}</div>
          <div className="stat-sub">{data.length} 个链接</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#EF9F27' }}>
          <div className="stat-label">总营收</div>
          <div className="stat-value" style={{ color: '#EF9F27' }}>¥{yuan(summary.totalRev)}</div>
          <div className="stat-sub">平均投产比 {summary.avgRatio ? summary.avgRatio.toFixed(2) : '—'}</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#CF1322' }}>
          <div className="stat-label">扣费后利润</div>
          <div className="stat-value" style={{ color: summary.totalNet >= 0 ? '#CF1322' : '#3F8600' }}>¥{yuan(summary.totalNet)}</div>
          <div className="stat-sub">利润 − 推广费</div>
        </div></Col>
        <Col xs={12} md={6}><div className="stat-card" style={{ '--sc': '#8C8178' }}>
          <div className="stat-label">判定分布</div>
          <div className="stat-value" style={{ fontSize: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 2 }}>
            {Object.entries(summary.dist).map(([k, n]) => n > 0 ? (
              <Tag key={k} color={VERDICT_META[k].color} style={{ margin: 0 }}>{k} {n}</Tag>
            ) : null)}
          </div>
          <div className="stat-sub">加预算 / 优化 / 砍链接 / 未投</div>
        </div></Col>
      </Row>

      {/* 列表 */}
      {data.length === 0 ? (
        <Card>
          <Empty description="还没有推广链接数据，点击右上角「新增推广链接」开始归因" />
        </Card>
      ) : (
        <Table
          className="link-attr-table"
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="middle"
          scroll={{ x: 1080 }}
          pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
        />
      )}

      {/* 新增/编辑弹窗 */}
      <Modal
        open={modalOpen}
        title={editingId != null ? '编辑推广链接' : '新增推广链接'}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 8 }}>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>推广链接 / 计划名称 <span style={{ color: 'var(--brand)' }}>*</span></label>
            <Input placeholder="如：三边封黑白系列-多多搜索" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>店铺（可多选，同品多店投放）</label>
            <Select
              mode="multiple"
              style={{ width: '100%' }} placeholder="选择店铺（不选 = 全部店铺）" allowClear showSearch
              value={form.shop}
              onChange={onShopsChange}
              options={shops.map(s => ({ label: s, value: s }))}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </div>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>
              统计日期范围（用于带出系统营收/利润，当前默认本月）
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DatePicker style={{ flex: 1 }} value={dateStart} onChange={onStartChange} allowClear={false} placeholder="开始日期" />
              <span style={{ color: '#8C8178' }}>~</span>
              {liveEnd ? (
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--brand-bg)', border: '1px solid var(--brand-border)', borderRadius: 6, padding: '4px 8px', color: 'var(--brand)', fontSize: 13, fontWeight: 600 }}>
                  实时滚动到今天
                </div>
              ) : (
                <DatePicker style={{ flex: 1 }} value={dateEnd} onChange={onEndChange} allowClear={false} placeholder="结束日期" />
              )}
            </div>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch size="small" checked={liveEnd} onChange={onLiveEndChange} />
              <span style={{ fontSize: 12, color: '#8C8178' }}>结束日期自动滚动到实时（推荐），每次刷新都算到最新订单，无需手动改日期</span>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>
              <AppstoreOutlined style={{ marginRight: 4, color: 'var(--brand)' }} />关联商品（多选，营收/利润自动合计）
              {prodLoading && <span style={{ color: 'var(--brand)', marginLeft: 8 }}>加载中…</span>}
            </label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="选择系统里的商品（可多选，同一链接多款），营收/利润自动合计填入"
              allowClear showSearch
              value={linkedSpecs}
              onChange={pickProducts}
              optionFilterProp="label"
              maxTagCount="responsive"
              options={productOpts.map(p => ({
                value: p.spec_code,
                label: `${p.product_name}（${p.spec_code}）`,
                desc: p,
              }))}
              optionRender={(o) => {
                const p = o.data.desc;
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.product_name}<span style={{ color: '#B3A79C', fontSize: 12 }}>（{p.spec_code}）</span></span>
                    <span style={{ flexShrink: 0, fontSize: 12, color: '#8C8178' }}>营收 ¥{yuan(p.revenue)} · {p.orders}单</span>
                  </div>
                );
              }}
              notFoundContent={prodLoading ? <Spin size="small" /> : '该日期范围/店铺下暂无订单'}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {skuGroups.length > 0 && (
                <Select
                  style={{ flex: 1, minWidth: 180 }}
                  placeholder="从 SKU 组合一键带入（可选）"
                  allowClear
                  value={undefined}
                  onChange={pickGroup}
                  options={skuGroups.map(g => ({
                    value: String(g.id),
                    label: g.name,
                    cnt: (g.spec_codes || []).length,
                  }))}
                  optionRender={(o) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.data.label}</span>
                      <span style={{ flexShrink: 0, fontSize: 12, color: '#8C8178' }}>{o.data.cnt} SKU</span>
                    </div>
                  )}
                />
              )}
              <Button icon={<SaveOutlined />} onClick={openSaveGroup}>把当前选中保存为组合</Button>
            </div>
          </div>
          <Row gutter={12}>
            <Col span={12}>
              <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>推广费（元）</label>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} value={form.fee} onChange={v => setForm(f => ({ ...f, fee: v ?? 0 }))} placeholder="0" />
            </Col>
            <Col span={12}>
              <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>营收（元）</label>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} value={form.revenue} onChange={v => setForm(f => ({ ...f, revenue: v ?? 0 }))} placeholder="0" />
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>利润（元，已扣售后、未扣推广费）</label>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} value={form.profit} onChange={v => setForm(f => ({ ...f, profit: v ?? 0 }))} placeholder="0" />
            </Col>
            <Col span={12}>
              <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>保本投产比</label>
              <InputNumber
                min={0} precision={2} style={{ width: '100%' }} value={form.breakEven} onChange={v => setForm(f => ({ ...f, breakEven: v ?? 0 }))} placeholder="0"
                addonAfter={<Tooltip title="保本投产比 = 营收 ÷ 利润（即 1 ÷ 毛利率），点按自动计算"><Button type="link" size="small" style={{ padding: 0, height: 'auto' }} icon={<ThunderboltOutlined />} onClick={autoBreakEven} /></Tooltip>}
              />
            </Col>
          </Row>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>
              <CalculatorOutlined style={{ marginRight: 4, color: 'var(--brand)' }} />从盈亏计算带入保本投产比（可选）
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder={pcOptions.length ? '选择盈亏计算里的商品，带入其「保本投产比」' : '盈亏计算里暂无记录'}
              allowClear showSearch
              value={undefined}
              onChange={pickBreakEvenFromCalc}
              optionFilterProp="label"
              options={pcOptions.filter(c => c.break_even_roi != null).map(c => ({
                value: String(c.id),
                label: c.name,
                roi: c.break_even_roi,
              }))}
              optionRender={(o) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.data.label}</span>
                  <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--brand)', fontWeight: 600 }}>保本 {o.data.roi}</span>
                </div>
              )}
              notFoundContent="盈亏计算里暂无已保存记录"
            />
          </div>
          <Divider style={{ margin: '4px 0' }} />
          <div style={{ background: 'var(--brand-bg)', border: '1px solid var(--brand-border)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#8C8178', lineHeight: 1.7 }}>
            <div><b style={{ color: '#26201C' }}>判定规则：</b></div>
            <div>· 推广费 = 0 → <Tag color="default" style={{ fontSize: 11, marginLeft: 2 }}>未投</Tag></div>
            <div>· 投产比 ≥ 保本线 → <Tag color="red" style={{ fontSize: 11, marginLeft: 2 }}>加预算</Tag></div>
            <div>· 扣费后利润 &gt; 0 → <Tag color="orange" style={{ fontSize: 11, marginLeft: 2 }}>优化</Tag></div>
            <div>· 否则 → <Tag color="green" style={{ fontSize: 11, marginLeft: 2 }}>砍链接</Tag></div>
          </div>
        </div>
      </Modal>

      {/* 保存 SKU 组合弹窗 */}
      <Modal
        open={groupModalOpen}
        title="保存为 SKU 组合"
        onCancel={() => setGroupModalOpen(false)}
        onOk={saveGroup}
        confirmLoading={groupSaving}
        okText="保存组合"
        cancelText="取消"
        width={420}
        destroyOnClose
      >
        <div style={{ paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 13, color: '#8C8178', display: 'block', marginBottom: 6 }}>组合名称 <span style={{ color: 'var(--brand)' }}>*</span></label>
            <Input placeholder="如：三边封黑白系列" value={groupName} onChange={e => setGroupName(e.target.value)} maxLength={40} />
          </div>
          <div style={{ background: 'var(--brand-bg)', border: '1px solid var(--brand-border)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#8C8178', lineHeight: 1.8 }}>
            <div style={{ fontWeight: 600, color: '#26201C', marginBottom: 4 }}>将保存以下 {linkedSpecs.length} 个 SKU：</div>
            {linkedSpecs.map(s => {
              const p = productOpts.find(x => x.spec_code === s);
              return <div key={s}>· {p ? p.product_name : s}<span style={{ color: '#B3A79C' }}>（{s}）</span></div>;
            })}
          </div>
          <div style={{ fontSize: 12, color: '#B3A79C' }}>保存后，下次新增推广链接时可从「SKU 组合」下拉一键带入。</div>
        </div>
      </Modal>

      {/* 详情弹窗（点击名称弹出） */}
      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => { setDetailOpen(false); openEdit(detailRow); }}>编辑该链接</Button>,
        ]}
        width={520}
      >
        {detailRow && detailView && (
          <div style={{ paddingTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <LinkOutlined style={{ fontSize: 20, color: 'var(--brand)' }} />
              <span style={{ fontSize: 17, fontWeight: 700, color: '#26201C', flex: 1, minWidth: 0 }}>{detailRow.name}</span>
              {(() => {
                const m = VERDICT_META[detailRow.verdict] || VERDICT_META['未投'];
                return <Tag color={m.color} style={{ fontSize: 13, padding: '2px 12px', borderRadius: 999 }}>{detailRow.verdict}</Tag>;
              })()}
            </div>

            {/* 投产比 vs 保本线 对比条 */}
            <div style={{ background: 'var(--brand-bg)', border: '1px solid var(--brand-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#8C8178', marginBottom: 10 }}>
                真实投产比 <b style={{ color: 'var(--brand)', fontSize: 15 }}>{detailView.ratio}</b>
                <span style={{ margin: '0 8px' }}>vs</span>
                保本线 <b style={{ color: '#26201C', fontSize: 15 }}>{detailView.be || '—'}</b>
              </div>
              <div style={{ position: 'relative', height: 20, background: 'var(--brand-border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: detailView.ratioPct + '%', background: 'linear-gradient(90deg,var(--grad-from),var(--brand))', borderRadius: 10, transition: 'width .3s' }} />
                {detailView.be > 0 && (
                  <div style={{ position: 'absolute', left: detailView.bePct + '%', top: -2, bottom: -2, width: 2, background: '#8C8178', transform: 'translateX(-50%)', zIndex: 1 }} title={`保本线 ${detailView.be}`} />
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#B3A79C', marginTop: 5 }}>
                <span>0</span>
                <span>投产比越高越好，越过灰线即「加预算」</span>
              </div>
            </div>

            <Descriptions bordered size="small" column={2} labelStyle={{ width: 110, background: 'var(--brand-bg)', color: '#8C8178' }}>
              <Descriptions.Item label="店铺" span={2}>{detailView.shopText || '全部店铺'}</Descriptions.Item>
              <Descriptions.Item label="关联商品" span={2}>
                {(() => {
                  const specs = Array.isArray(detailRow.spec_codes) ? detailRow.spec_codes : (detailRow.spec_code ? [detailRow.spec_code] : []);
                  if (!specs.length) return <span style={{ color: '#B3A79C' }}>未关联</span>;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {specs.map(s => <span key={s} style={{ fontSize: 12 }}>{s}</span>)}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="统计窗口" span={2}>
                {detailRow.date_start || detailRow.date_end
                  ? `${detailRow.date_start || '…'} ~ ${detailRow.date_end || '…'}`
                  : <span style={{ color: '#B3A79C' }}>全部发货时间</span>}
              </Descriptions.Item>
              <Descriptions.Item label="推广费"><span style={{ fontWeight: 600 }}>¥{yuan(detailRow.fee)}</span></Descriptions.Item>
              <Descriptions.Item label="营收"><span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(detailRow.revenue)}</span></Descriptions.Item>
              <Descriptions.Item label="利润(未扣费)">
                <span className={(Number(detailRow.profit) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(detailRow.profit)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="扣费后利润">
                <span className={(Number(detailRow.netProfit) || 0) >= 0 ? 'profit-positive' : 'profit-negative'}>¥{yuan(detailRow.netProfit)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="真实投产比"><span style={{ fontWeight: 700 }}>{detailRow.ratio}</span></Descriptions.Item>
              <Descriptions.Item label="保本投产比"><span style={{ color: '#8C8178' }}>{detailRow.breakEven || '—'}</span></Descriptions.Item>
              <Descriptions.Item label="付费占比">{detailRow.payPct}%</Descriptions.Item>
              <Descriptions.Item label="判定">{(VERDICT_META[detailRow.verdict] || {}).desc || '—'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
}
